import { supabase } from "@/integrations/supabase/client";

export interface PagePermissions {
  digital_closet?: boolean;
  style_magazine?: boolean;
  shopping_recommendations?: boolean;
  report?: boolean;
}

/**
 * Get user's page permissions from the database
 */
export const getUserPagePermissions = async (userId?: string): Promise<PagePermissions> => {
  try {

    // If no userId provided, get current user
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          digital_closet: false,
          style_magazine: false,
          shopping_recommendations: false,
          report: false
        };
      }
      userId = user.id;
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('page_permissions, is_admin')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('🔐 Error fetching user permissions:', error);
      return {
        digital_closet: false,
        style_magazine: false,
        shopping_recommendations: false,
        report: false
      };
    }


    // Admins have access to all pages (TEMPORARILY DISABLED FOR TESTING)
    // if ((profile as any).is_admin) {
    //   console.log('🔐 User is admin, granting all permissions');
    //   return {
    //     digital_closet: true,
    //     style_magazine: true,
    //     shopping_recommendations: true
    //   };
    // }

    // Return user's permissions - digital_closet defaults to true for all authenticated
    // users; the closet report defaults to hidden until an admin grants it.
    const permissions = {
      digital_closet: (profile as any).page_permissions?.digital_closet ?? true,
      style_magazine: (profile as any).page_permissions?.style_magazine ?? false,
      shopping_recommendations: (profile as any).page_permissions?.shopping_recommendations ?? false,
      report: (profile as any).page_permissions?.report ?? false
    };

    return permissions;

  } catch (error) {
    console.error('🔐 Error in getUserPagePermissions:', error);
    return {
      digital_closet: false,
      style_magazine: false,
      shopping_recommendations: false,
      report: false
    };
  }
};

/**
 * Strict read of the closet permission for the "restricted until permitted"
 * closet on /dashboard1. getUserPagePermissions() fails OPEN on a missing
 * `digital_closet` key (`?? true`) -- right for the legacy /digital-closet page,
 * exactly wrong here, where a missing key must mean "not yet". Fails closed.
 */
export const isDigitalClosetUnlocked = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('page_permissions')
      .eq('id', userId)
      .maybeSingle();
    if (error) {
      console.error('🔐 Error reading closet permission:', error);
      return false;
    }
    return (data?.page_permissions as PagePermissions | null)?.digital_closet === true;
  } catch (error) {
    console.error('🔐 Error in isDigitalClosetUnlocked:', error);
    return false;
  }
};

/**
 * Check if user has permission for a specific page
 */
export const hasPagePermission = async (page: keyof PagePermissions, userId?: string): Promise<boolean> => {
  const permissions = await getUserPagePermissions(userId);
  return permissions[page] ?? false;
};

/**
 * Hook-style permission checker for React components
 */
export const usePagePermissions = () => {
  return {
    getUserPagePermissions,
    hasPagePermission,
  };
};
